import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Stack,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
} from "@mui/material";
import {
  Edit,
  Delete,
  KeyboardArrowRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { getContrastingTextColor } from "services/utils/colorChecker";
import { usePayrollCycleRun } from "../../PayrollCycleRunContext";
import { themes } from "services/constants";
import { stickyHeader } from "services/constants";
import { weekDaysName, legendData } from "pages/PayRoll/PayRollHeaders";
import { Loader } from "components/Loader";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { useForm } from "react-hook-form";
import { CustomTextBox } from "components/CustomComponents";
import * as Mui from "@mui/material";
import { InfoMessage } from "components/CustomComponents/CustomButton";

const formatAttendanceData = (data) => {
  console.log(data);
  const flatData = data.flat();
  const result = [];
  let week = [];
  let dayCursor = 1;

  flatData.forEach((day) => {
    const date = new Date(day.date);
    let jsDay = date.getDay();
    jsDay = jsDay === 0 ? 7 : jsDay;

    while (dayCursor < jsDay) {
      week.push({
        legend: "-",
        day_number: "",
        colour_code: themes.lightShadow,
      });
      dayCursor++;
    }

    week.push({
      legend: day.legend,
      day_number: day.day_number,
      colour_code:
        day.colour_code === "#ffffff" ? "#272626ff" : day.colour_code,
    });
    dayCursor++;

    if (week.length === 7) {
      result.push(week);
      week = [];
      dayCursor = 1;
    }
  });

  while (week.length > 0 && week.length < 7) {
    week.push({
      legend: "-",
      day_number: "",
      colour_code: themes.lightShadow,
    });
  }

  if (week.length) result.push(week);
  return result;
};

// Sub-components
const DataTable = ({
  data,
  columns,
  emptyMessage = "No data available",
  renderRow,
  summaryRow,
}) => (
  <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor }}>
    <Table size="small" className="border">
      <TableHead sx={stickyHeader}>
        <TableRow>
          {columns.map((column, index) => (
            <TableCell key={index}>{column}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      {!data || data.length === 0 ? (
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length} align="center">
              <Alert severity="info">{emptyMessage}</Alert>
            </TableCell>
          </TableRow>
        </TableBody>
      ) : (
        <TableBody>
          {data.map((item, index) => renderRow(item, index))}
          {summaryRow && summaryRow()}
        </TableBody>
      )}
    </Table>
  </TableContainer>
);

const AttendanceCalendar = ({ attendanceDayDetails }) => (
  <>
    <Grid container sx={{ paddingTop: 1 }}>
      <SectionHeader>Attendance</SectionHeader>
    </Grid>

    <Grid container sx={{ paddingTop: 2 }}>
      {weekDaysName.map((day) => (
        <Grid
          key={day}
          item
          xs={1.7}
          sx={{
            padding: 2,
            border: `1px solid ${themes.sidebarDivider}`,
            backgroundColor: "#f5f5f5",
            textAlign: "center",
          }}
        >
          <Typography fontWeight={600} color={themes?.textColor || "#333"}>
            {day}
          </Typography>
        </Grid>
      ))}
    </Grid>

    {attendanceDayDetails?.map((week, weekIndex) => (
      <Grid container key={`week-${weekIndex}`}>
        <Grid container>
          {week?.map((day, dayIndex) => (
            <Grid
              key={`day-${weekIndex}-${dayIndex}`}
              item
              xs={1.7}
              sx={{
                padding: 1,
                border: `1px solid ${themes.sidebarDivider}`,
                borderBottom: "none",
                backgroundColor: "#ffffff",
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography fontWeight="bold" color={themes?.textColor || "#333"}>
                {day.day_number}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Grid container>
          {week?.map((day, dayIndex) => (
            <Grid
              key={`status-${weekIndex}-${dayIndex}`}
              item
              xs={1.7}
              sx={{
                padding: 1,
                border: `1px solid ${themes.sidebarDivider}`,
                borderTop: "none",
                backgroundColor: day.colour_code,
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                fontWeight="bold"
                textAlign="center"
                color={getContrastingTextColor(day?.colour_code) || "#333"}
              >
                {day.legend}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Grid>
    ))}
  </>
);

const Legend = () => (
  <Grid container spacing={3} sx={{ pt: 3, justifyContent: "center" }}>
    {legendData.map((legend, index) => (
      <Grid item key={legend.label}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: 1,
              backgroundColor: legend.color,
              border: "1px solid #ccc",
            }}
          />
          <Typography fontSize={14} color="#333" fontWeight={600}>
            {legend.label}
          </Typography>
        </Box>
      </Grid>
    ))}
  </Grid>
);

const SectionHeader = ({ children }) => (
  <Typography
    sx={{
      fontSize: 16,
      fontWeight: "bold",
      color: "#556774",
      mt: 2,
    }}
  >
    {children}
  </Typography>
);

// Main Component
export const AttendanceDetails = ({ setRecallEmployee }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const {
    attendanceDayDetails,
    setAttendanceDayDetails,
    setAttendanceData,
    payScheduleId,
    userId,
    payScheduleData,
  } = usePayrollCycleRun();
  const {
    result: attendanceDetails,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    `${domain}payroll/operations/payrollprocessing/payrollruncycle/individual-employee-payrun/?pay_schedule_id=${payScheduleId}&user_id=${userId}`
  );
  const {
    watch,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      reason: "",
      adjustmentType: "add shift",
      no_of_shift_adjust: "",
    },
  });

  const [isPenalty, setIsPenalty] = useState(false);
  const [isSummary, setIsSummary] = useState(false);
  const [isOtherEarnings, setIsOtherEarnings] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(false);
  const isEditable =
    payScheduleData?.status === "Submitted" ||
    payScheduleData?.status === "Approved";

  const summaryData = useMemo(
    () => attendanceDetails?.attendance?.summary?.[0] || {},
    [attendanceDetails]
  );

  useEffect(() => {
    if (!loading && attendanceDetails) {
      const timeline =
        attendanceDetails?.attendance?.attendance_timeline?.[0]?.attendance ||
        [];
      const formattedAttendance = formatAttendanceData(timeline);
      setAttendanceDayDetails(formattedAttendance);
      setAttendanceData(attendanceDetails);
    }
  }, [attendanceDetails, loading, setAttendanceData, setAttendanceDayDetails]);

  if (payScheduleData?.status === "Draft") {
    return (
      <Alert severity="info" fullWidth>
        Payroll run has not been initiated to the user yet.
      </Alert>
    );
  }

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
    setSelectedData([]);
    reset();
  };

  const handlePenalty = async () => {
    const payload = {
      penalty_id: selectedData.id,
      pay_schedule_id: payScheduleId,
    };
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/remove_penalty/`,
      "PUT",
      payload
    );
    if (response.ok) {
      showToast("success", "Penalty removed successfully");
      setShowConfirmationDialog(false);
      reCallApi(true);
      setRecallEmployee(true);
    }
    watch("reason");
  };

  const onSubmit = async (data) => {
    const payload = {
      user_id: userId,
      pay_schedule_id: payScheduleId,
      adjustment_type: watch("adjustmentType"),
      adjusted_value: watch("no_of_shift_adjust"),
      payrun_basis: attendanceDetails.calculation_based_on,
      reason: watch("reason"),
    };
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/update_manual_adjustment/`,
      "PUT",
      payload
    );
    if (response.ok) {
      showToast("success", "Adjustment added successfully");
      setShowConfirmationDialog(false);
      reCallApi(true);
    }
  };

  const hanldeOtherEarnings = async () => {
    const payload = {
      pay_schedule_id: payScheduleId,
      earning_id: selectedData.id,
    };
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/remove_other_earnings/`,
      "PUT",
      payload
    );
    if (response.ok) {
      showToast("success", "Other earning removed successfully");
      setShowConfirmationDialog(false);
      reCallApi(true);
    }
    setShowConfirmationDialog(false);
  };

  const handleReason = (event) => {
    const reason = event.target.value;
    if (reason) {
      setValue("reason", event.target.value);
    } else {
      setValue("reason", "");
    }
  };

  const handleAdjustShift = (event) => {
    const validPattern = /^[0-9]?(\.\d{0,2})?$/;
    if (validPattern.test(event.target.value)) {
      setValue("no_of_shift_adjust", event.target.value);
    } else {
      setValue("no_of_shift_adjust", "");
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <Grid container sx={{ p: 1, backgroundColor: themes?.primary }}>
      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCancelClose}
        onConfirm={() => {
          if (isPenalty) return handlePenalty();
          if (isSummary) return handleSubmit(onSubmit)();
          return hanldeOtherEarnings();
        }}
        title={isSummary ? "Adjustment" : "Confirm Deletion"}
        confirmText={isSummary ? "Add" : "Delete"}
        cancelText="Cancel"
        showChildren={true}
      >
        {(isPenalty || isOtherEarnings) && (
          <Box sx={{ mb: 1 }}>
            <Box>
              <Typography>
                {`Are you sure you want to exclude ${
                  selectedData?.type || "this item"
                } from
               the current payroll.`}
              </Typography>
            </Box>
          </Box>
        )}
        {isSummary && (
          <>
            <Grid container spacing={1} sx={{ py: 1 }}>
              <Grid item xs={3.5}></Grid>
              <Grid item xs={4}>
                <Typography color="text.secondary" display="flex" gap={2}>
                  <span>Name</span>
                  <strong>{attendanceDetails?.employee_name ?? "-"}</strong>
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography color="text.secondary" display="flex" gap={2}>
                  <span>Payable Shift</span>
                  <strong>{summaryData.total_payable_shift ?? 0}</strong>
                </Typography>
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography sx={{ width: 180, fontWeight: "bold" }}>
                Adjustment type
              </Typography>

              <Box sx={{ display: "flex", mt: 1 }}>
                <RadioGroup
                  row
                  onChange={(e) => {
                    setValue("adjustmentType", e.target.value);
                  }}
                  defaultValue={watch("adjustmentType")}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="Add Shift"
                    value="add shift"
                  />

                  <FormControlLabel
                    control={<Radio />}
                    label="Deduct Shift"
                    value="deduct shift"
                  />
                </RadioGroup>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography sx={{ width: 250, fontWeight: "bold" }}>
                No of Shift to Adjust
              </Typography>

              <CustomTextBox
                rules={{
                  ...register("no_of_shift_adjust", {
                    required: "No of shift to adjust is required",
                  }),
                }}
                selectedValue={watch("no_of_shift_adjust")}
                updateDataFunction={handleAdjustShift}
                placeHolder="Enter adjustment shift"
                errorMessage={
                  errors?.no_of_shift_adjust?.message
                    ? errors?.no_of_shift_adjust?.message
                    : ""
                }
                textBoxError={errors?.no_of_shift_adjust ? true : false}
              />
            </Box>
          </>
        )}

        <Box
          sx={{
            ...(isSummary && {
              display: "flex",
              alignItems: "center",
              mb: 1,
            }),
          }}
        >
          <Typography
            sx={{
              ...(isSummary
                ? {
                    width: 250,
                    fontWeight: "bold",
                  }
                : { fontWeight: "bold", mb: 0.5 }),
            }}
          >
            Reason (Optional)
          </Typography>
          <CustomTextBox
            type="multiline"
            rules={{
              ...register("reason"),
            }}
            selectedValue={watch("reason")}
            updateDataFunction={handleReason}
            placeHolder={
              isSummary
                ? "Enter a reason for adjustment"
                : "Enter a reason for deletion"
            }
          />
        </Box>
      </ConfirmationDialog>
      {/* Summary Stats */}
      <Grid container spacing={1} sx={{ py: 1 }}>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">
            Total Days <strong>{attendanceDetails?.total_days ?? "--"}</strong>
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">
            Present Days{" "}
            <strong>{attendanceDetails?.present_days ?? "--"}</strong>
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">
            Calculated Based On{" "}
            <strong>{attendanceDetails?.calculation_based_on ?? "--"}</strong>
          </Typography>
        </Grid>
      </Grid>
      <SectionHeader>Earnings</SectionHeader>
      <DataTable
        data={attendanceDetails?.attendance?.earnings}
        columns={[
          <>Shifts</>,
          "Count",
          "Hours",
          "Basis",
          "Qty",
          "Factor",
          "Adjusted",
        ]}
        renderRow={(data, index) => (
          <React.Fragment key={index}>
            <TableRow>
              <TableCell>{data?.type}</TableCell>
              <TableCell>{data?.count ?? "0"}</TableCell>
              <TableCell>{data?.hours ?? "0"}</TableCell>
              <TableCell>{data?.based_on}</TableCell>
              <TableCell>{data?.quantity}</TableCell>
              <TableCell>{data?.factor}</TableCell>
              <TableCell>{data?.adjusted}</TableCell>
            </TableRow>
          </React.Fragment>
        )}
        summaryRow={() => (
          <>
            <TableRow>
              <TableCell>
                <strong>Base </strong>
              </TableCell>
              <TableCell colSpan={5} />
              <TableCell>
                <strong>{summaryData.base_shift ?? 0}</strong>
              </TableCell>
            </TableRow>
          </>
        )}
      />
      <SectionHeader>Penalty</SectionHeader>
      <DataTable
        data={attendanceDetails?.attendance?.penalties}
        columns={[
          <>Penalty</>,
          "Count",
          "Hours",
          "Basis",
          "Qty",
          "Factor",
          "Adjusted",
          "",
        ]}
        renderRow={(data, index) => (
          <React.Fragment key={index}>
            <TableRow>
              <TableCell>{data?.type}</TableCell>
              <TableCell>{data?.count}</TableCell>
              <TableCell>{data?.hours || "0"}</TableCell>
              <TableCell>{data?.based_on}</TableCell>
              <TableCell>{data?.quantity}</TableCell>
              <TableCell>{data?.factor}</TableCell>
              <TableCell>{data?.adjusted}</TableCell>
              <TableCell>
                <IconButton
                  disabled={isEditable}
                  onClick={() => {
                    setShowConfirmationDialog(true);
                    setSelectedData(data);
                    setIsPenalty(true);
                    setIsOtherEarnings(false);
                    setIsSummary(false);
                  }}
                >
                  <Delete
                    color={isEditable ? themes.disabledColor : "error"}
                    sx={{ fontSize: 20, cursor: "pointer" }}
                  />
                </IconButton>
              </TableCell>
            </TableRow>
          </React.Fragment>
        )}
        summaryRow={() => (
          <>
            <TableRow>
              <TableCell>
                <strong>Penalty Shift</strong>
              </TableCell>
              <TableCell colSpan={5} />
              <TableCell>
                <strong>{summaryData.penalty_shift ?? 0}</strong>
              </TableCell>
            </TableRow>
          </>
        )}
      />
      <SectionHeader>Summary</SectionHeader>
      <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor }}>
        <Table size="small" className="border">
          <TableBody>
            <TableRow>
              <TableCell>
                <strong>Base {attendanceDetails.calculation_based_on}</strong>
              </TableCell>
              <TableCell>{summaryData.base_shift ?? 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>
                  Penalty {attendanceDetails.calculation_based_on}
                </strong>
              </TableCell>
              <TableCell>{summaryData.penalty_shift ?? 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Manual Adjustment (+/-)</strong>
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>
                    {summaryData.adjustement_type === "add shift" ? "+" : "-"}
                    {summaryData.manual_adjusted ?? 0}
                  </span>
                  <IconButton
                    disabled={isEditable}
                    onClick={() => {
                      setShowConfirmationDialog(true);
                      setSelectedData(summaryData);
                      setValue("adjustmentType", summaryData?.adjustement_type);
                      setValue(
                        "no_of_shift_adjust",
                        parseInt(summaryData?.manual_adjusted)
                      );
                      setIsSummary(true);
                      setIsPenalty(false);
                      setIsOtherEarnings(false);
                    }}
                  >
                    <Edit
                      color={isEditable ? themes.disabledColor : "primary"}
                      sx={{ fontSize: 20, cursor: "pointer" }}
                    />
                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>
                  Total Payable {attendanceDetails.calculation_based_on}
                </strong>
              </TableCell>
              <TableCell>
                <strong>{summaryData.total_payable_shift ?? 0}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <SectionHeader>Other Earnings</SectionHeader>
      <DataTable
        data={attendanceDetails?.attendance?.other_earnings}
        columns={[
          <>Earnings</>,
          "Count",
          "Hours",
          "Basis",
          "Qty",
          "Factor",
          "Adjusted",
          "",
        ]}
        renderRow={(data, index) => (
          <TableRow key={index}>
            <TableCell sx={{ width: 150 }}>{data?.type}</TableCell>
            <TableCell>{data?.count}</TableCell>
            <TableCell>{data?.hours ?? "0"}</TableCell>
            <TableCell>{data?.based_on}</TableCell>
            <TableCell>{data?.quantity}</TableCell>
            <TableCell>{data?.factor}</TableCell>
            <TableCell>{data?.adjusted}</TableCell>
            <TableCell>
              <IconButton
                disabled={isEditable}
                onClick={() => {
                  setShowConfirmationDialog(true);
                  setSelectedData(data);
                  setIsOtherEarnings(true);
                  setIsPenalty(false);
                  setIsSummary(false);
                }}
              >
                <Delete
                  color={isEditable ? themes.disabledColor : "error"}
                  sx={{ fontSize: 20, cursor: "pointer" }}
                />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />
      <AttendanceCalendar attendanceDayDetails={attendanceDayDetails} />
      <Legend />
      <SectionHeader>Manual Adjustment History</SectionHeader>
      <Mui.TableContainer
        component={Mui.Paper}
        sx={{ mt: 2, width: "100%", maxHeight: 850 }}
      >
        <Mui.Table>
          <Mui.TableHead
            sx={{
              backgroundColor: themes.primary,
              position: "sticky",
              top: 0,
              zIndex: 10,
              "& .MuiTableCell-root": {
                height: "15px",
                padding: "10px 20px",
                color: themes.headLine,
                fontWeight: "bold",
              },
            }}
          >
            <Mui.TableRow>
              <Mui.TableCell />
              <Mui.TableCell>Adjusted by</Mui.TableCell>
              <Mui.TableCell>Adjusted on</Mui.TableCell>
              <Mui.TableCell>Adjustment</Mui.TableCell>
              <Mui.TableCell>Reason</Mui.TableCell>
            </Mui.TableRow>
          </Mui.TableHead>

          <Mui.TableBody>
            {attendanceDetails?.attendance?.manual_adjustment_history
              ?.length === 0 ? (
              <InfoMessage
                dataLength={
                  attendanceDetails?.attendance?.manual_adjustment_history
                }
              />
            ) : (
              attendanceDetails?.attendance?.manual_adjustment_history?.map(
                (row, index) => (
                  <React.Fragment key={index}>
                    <Mui.TableRow>
                      <Mui.TableCell>
                        <Mui.IconButton
                          onClick={() =>
                            setExpandedRows(
                              expandedRows === index ? null : index
                            )
                          }
                        >
                          {expandedRows === index ? (
                            <KeyboardArrowDown
                              style={{ color: themes.headLine }}
                            />
                          ) : (
                            <KeyboardArrowRight
                              style={{ color: themes.headLine }}
                            />
                          )}
                        </Mui.IconButton>
                      </Mui.TableCell>

                      <Mui.TableCell>
                        {row.adjusted_by ? row.adjusted_by : "N/A"}
                      </Mui.TableCell>
                      <Mui.TableCell>
                        {row.modifiedat ? row.modifiedat : "N/A"}
                      </Mui.TableCell>
                      <Mui.TableCell>
                        {row.adjustment ? row.adjustment : "N/A"}
                      </Mui.TableCell>
                      <Mui.TableCell>
                        {row.reason ? row.reason : "N/A"}
                      </Mui.TableCell>
                    </Mui.TableRow>

                    {/* Expanded Section */}
                    <Mui.TableRow>
                      <Mui.TableCell
                        colSpan={5}
                        sx={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Mui.Collapse
                          in={expandedRows === index}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Mui.Box sx={{ margin: 2 }}>
                            <Mui.Table size="small">
                              <Mui.TableHead
                                sx={{
                                  backgroundColor: "#F7F7F7",
                                  "& .MuiTableCell-root": {
                                    fontWeight: "bold",
                                  },
                                }}
                              >
                                <Mui.TableRow>
                                  <Mui.TableCell>Other Earnings</Mui.TableCell>
                                  <Mui.TableCell>Count</Mui.TableCell>
                                  <Mui.TableCell>Hours</Mui.TableCell>
                                  <Mui.TableCell>Basis</Mui.TableCell>
                                  <Mui.TableCell>Qty</Mui.TableCell>
                                  <Mui.TableCell>Factor</Mui.TableCell>
                                  <Mui.TableCell>Adjusted</Mui.TableCell>
                                </Mui.TableRow>
                              </Mui.TableHead>

                              <Mui.TableBody>
                                {row.other_earnings?.map((earning, idx) => (
                                  <Mui.TableRow key={idx}>
                                    <Mui.TableCell>
                                      {earning.title}
                                    </Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.count}
                                    </Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.hours}
                                    </Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.basis}
                                    </Mui.TableCell>
                                    <Mui.TableCell>{earning.qty}</Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.factor}
                                    </Mui.TableCell>
                                    <Mui.TableCell sx={{ color: "blue" }}>
                                      {earning.adjusted}
                                    </Mui.TableCell>
                                  </Mui.TableRow>
                                ))}
                              </Mui.TableBody>
                            </Mui.Table>
                          </Mui.Box>
                        </Mui.Collapse>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  </React.Fragment>
                )
              )
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>
    </Grid>
  );
};
